import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import { User, Product, Decision, Announcement, Discussion } from '../types';

interface DashboardStats {
  activeDecisions: number;
  productsListed: number;
  teamMembers: number;
  monthlyGrowth: string;
}

interface ActivityItem {
  id: string;
  type: 'decision' | 'product' | 'announcement' | 'discussion';
  title: string;
  time: Date;
  user: string;
}

export const useDashboardData = (userId: string, isAdmin: boolean = false) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    activeDecisions: 0,
    productsListed: 0,
    teamMembers: 0,
    monthlyGrowth: '0%',
  });
  const [recentActivities, setRecentActivities] = useState<ActivityItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch stats
        const [decisionsSnapshot, productsSnapshot, usersSnapshot] = await Promise.all([
          getDocs(collection(db, 'decisions')),
          getDocs(collection(db, 'products')),
          getDocs(collection(db, 'users')),
        ]);

        // Calculate stats
        const activeDecisions = decisionsSnapshot.docs.filter(
          doc => doc.data().isActive
        ).length;

        const productsListed = isAdmin 
          ? productsSnapshot.size 
          : productsSnapshot.docs.filter(doc => doc.data().addedBy === userId).length;

        const teamMembers = usersSnapshot.size;

        // Calculate monthly growth (simplified example)
        const monthlyGrowth = calculateMonthlyGrowth(productsSnapshot.docs);

        setStats({
          activeDecisions,
          productsListed,
          teamMembers,
          monthlyGrowth: `${monthlyGrowth}%`,
        });

        // Fetch recent activities
        const activities: ActivityItem[] = [];
        
        // Get recent decisions
        const decisionsQuery = query(
          collection(db, 'decisions'),
          orderBy('createdAt', 'desc'),
          limit(3)
        );
        const recentDecisions = await getDocs(decisionsQuery);
        recentDecisions.forEach(doc => {
          const data = doc.data();
          activities.push({
            id: doc.id,
            type: 'decision',
            title: data.title,
            time: data.createdAt.toDate(),
            user: data.createdByName,
          });
        });

        // Get recent products (only user's products if not admin)
        const productsQuery = isAdmin 
          ? query(collection(db, 'products'), orderBy('createdAt', 'desc'), limit(3))
          : query(collection(db, 'products'), 
                  where('addedBy', '==', userId), 
                  orderBy('createdAt', 'desc'), 
                  limit(3));
        
        const recentProducts = await getDocs(productsQuery);
        recentProducts.forEach(doc => {
          const data = doc.data();
          activities.push({
            id: doc.id,
            type: 'product',
            title: `${data.name} added to ${data.platform}`,
            time: data.createdAt.toDate(),
            user: data.addedByName,
          });
        });

        // Get recent announcements
        const announcementsQuery = query(
          collection(db, 'announcements'),
          orderBy('createdAt', 'desc'),
          limit(3)
        );
        const recentAnnouncements = await getDocs(announcementsQuery);
        recentAnnouncements.forEach(doc => {
          const data = doc.data();
          activities.push({
            id: doc.id,
            type: 'announcement',
            title: data.title,
            time: data.createdAt.toDate(),
            user: data.createdByName,
          });
        });

        // Get recent discussions
        const discussionsQuery = query(
          collection(db, 'discussions'),
          orderBy('createdAt', 'desc'),
          limit(3)
        );
        const recentDiscussions = await getDocs(discussionsQuery);
        recentDiscussions.forEach(doc => {
          const data = doc.data();
          activities.push({
            id: doc.id,
            type: 'discussion',
            title: data.message.length > 30 ? `${data.message.substring(0, 30)}...` : data.message,
            time: data.createdAt.toDate(),
            user: data.authorName,
          });
        });

        // Sort activities by time and take the 4 most recent
        const sortedActivities = activities
          .sort((a, b) => b.time.getTime() - a.time.getTime())
          .slice(0, 4);

        setRecentActivities(sortedActivities);
        setError(null);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchData();
    }
  }, [userId, isAdmin]);

  return { stats, recentActivities, loading, error };
};

// Helper function to calculate monthly growth (simplified example)
function calculateMonthlyGrowth(products: any[]): number {
  const now = new Date();
  const lastMonth = new Date();
  lastMonth.setMonth(lastMonth.getMonth() - 1);
  
  const currentMonthProducts = products.filter(doc => {
    const productDate = doc.data().createdAt.toDate();
    return productDate.getMonth() === now.getMonth() && 
           productDate.getFullYear() === now.getFullYear();
  });
  
  const lastMonthProducts = products.filter(doc => {
    const productDate = doc.data().createdAt.toDate();
    return productDate.getMonth() === lastMonth.getMonth() && 
           productDate.getFullYear() === lastMonth.getFullYear();
  });
  
  if (lastMonthProducts.length === 0) return currentMonthProducts.length > 0 ? 100 : 0;
  
  return Math.round(
    ((currentMonthProducts.length - lastMonthProducts.length) / lastMonthProducts.length) * 100
  );
}
